import {makeAutoObservable} from "mobx"
import {api, catchApi} from "../../api";
import {
    PreservationMessageDto,
    VehicleDataAcquisitionDetailDto,
    VehicleDataAcquisitionDocumentDto,
    VehicleDataAcquisitionStatusDto
} from "../../api/vda";
import {LeadingLicensePlateType, VdaFormData} from "../../components/page/vda/VdaForm";
import {VdaCustomerItem, vdaCustomerStore} from "./VdaCustomerStore";
import {validationStore} from "../ValidationStore";
import {equals} from "../../utils";
import {VdaPlacementItem, vdaPlacementStore} from "./VdaPlacementStore";
import {sortDocuments, updateLicensePlates} from "./VdaUtils";

class VdaDetailStore {
    get vinCheckDigit(): string | undefined {
        return this._vinCheckDigit;
    }

    set vinCheckDigit(value: string | undefined) {
        this._vinCheckDigit = value;
    }
    get licensePlate(): string {
        return this._licensePlate;
    }

    set licensePlate(value: string) {
        this._licensePlate = value;
    }
    get placement(): VdaPlacementItem | undefined {
        return this._placement;
    }

    set placement(value: VdaPlacementItem | undefined) {
        this._placement = value;
    }
    get messages(): PreservationMessageDto[] {
        return this._messages;
    }

    set messages(value: PreservationMessageDto[]) {
        this._messages = value;
    }


    private get initFormData(): VdaFormData | undefined {
        return this._initFormData;
    }

    private set initFormData(value: VdaFormData | undefined) {
        this._initFormData = value;
    }

    get valid(): boolean {
        return this._valid;
    }

    set valid(value: boolean) {
        this._valid = value;
    }

    get saving(): boolean {
        return this._saving;
    }

    set saving(value: boolean) {
        this._saving = value;
    }

    get customer(): VdaCustomerItem | undefined {
        return this._customer;
    }

    set customer(value: VdaCustomerItem | undefined) {
        vdaPlacementStore.init(value?.id).finally()
        if (!value) {
            this.placement = undefined
        }
        this._customer = value;
    }

    get documents(): VehicleDataAcquisitionDocumentDto[] {
        return this._documents;
    }

    set documents(value: VehicleDataAcquisitionDocumentDto[]) {
        this._documents = value;
    }

    get vin(): string {
        return this._vin;
    }

    set vin(value: string) {
        this._vin = value;
    }

    private get data(): VehicleDataAcquisitionDetailDto | undefined {
        return this._data;
    }

    private set data(value: VehicleDataAcquisitionDetailDto | undefined) {
        this._data = value;
    }

    get id(): string | undefined {
        return this._id;
    }

    set id(value: string | undefined) {
        this._id = value;
    }

    get editable(): boolean {
        return VehicleDataAcquisitionStatusDto.ACQUISITION === this.data?.data.status
    }

    get reopenable(): boolean {
        return VehicleDataAcquisitionStatusDto.ERROR === this.data?.data.status
    }

    get status(): VehicleDataAcquisitionStatusDto {
        return this.data?.data.status || VehicleDataAcquisitionStatusDto.ACQUISITION
    }

    private _vin: string = ''
    private _vinCheckDigit: string | undefined
    private _licensePlate: string = ''
    private _customer: VdaCustomerItem | undefined
    private _placement: VdaPlacementItem | undefined
    private _documents: VehicleDataAcquisitionDocumentDto[] = []


    private _id: string | undefined
    private _data: VehicleDataAcquisitionDetailDto | undefined
    private _messages: PreservationMessageDto[] = []
    private _initFormData: VdaFormData | undefined
    private _saving: boolean = false
    private _valid: boolean = false

    constructor() {
        makeAutoObservable(this)
    }

    async load(): Promise<VehicleDataAcquisitionDetailDto | undefined> {
        const id = this.id!!
        const result = await catchApi(async () => await api.vda.api.getVehicleDataAcquisition(id))

        await this.loadPreservationMessages()
        await vdaPlacementStore.init(result?.data.data.customerId)

        this.data = result?.data
        this.reset()

        return result?.data
    }

    async loadPreservationMessages(): Promise<void> {
        const id = this.id!!
        const result = await catchApi(async () => await api.vda.api.getPreservationMessages(id))
        this.messages = result?.data || []
    }

    async save(): Promise<string | undefined> {
        const dto = this.buildDto()
        this.saving = true
        const id = this.id!!
        const response = await catchApi(async () => await api.vda.api.updateVehicleDataAcquisition(id, dto))
        this.saving = false

        if (response?.data) {
            this.data = response.data
            this.reset()
            return id
        }
        return undefined
    }

    async transfer(): Promise<string | undefined> {
        this.saving = true

        await this.validate()
        if (!this.valid) {
            this.saving = false
            return undefined
        }

        const id = this.id!!
        const response = await catchApi(async () => await api.vda.api.transferVehicleDataAcquisition(id))

        this.saving = false

        if (response?.data) {
            this.data = response.data
            this.reset()
            return id
        }
        return undefined
    }

    async reopen(): Promise<string | undefined> {
        this.saving = true

        const id = this.id!!
        const response = await catchApi(async () => await api.vda.api.reopenVehicleDataAcquisition(id))

        this.saving = false

        if (response?.data) {
            this.data = response.data
            this.reset()
            await this.validate()
            return id
        }
        return undefined
    }

    async validate() {
        const build = this.buildDto()
        const dto = build.data

        await validationStore.validate(api.vda.api.validateVehicleDataAcquisition, dto)

        this.valid = Boolean(validationStore.valid)
    }

    private buildDto(): VehicleDataAcquisitionDetailDto {
        const {
            vin,
            documents,
            placement,
            customer,
            vinCheckDigit
        } = this
        return {
            audit: this.data?.audit,
            data: {
                vin: vin,
                vinCheckDigit: vinCheckDigit,
                placementProcessId: placement?.id!!,
                documents:documents,
                customerId: customer?.id!!,
            }
        }
    }

    get dirty(): boolean {
        return !equals(this.formData, this.initFormData);
    }

    get leadingLicensePlate() : LeadingLicensePlateType | undefined {
        const availableTypes = this.documents.map(d => d.type)
        const priorityOrder: LeadingLicensePlateType[] = ['COR_I', 'COR_II', 'LICENSE_PLATE_FRONT', 'LICENSE_PLATE_REAR']
        return priorityOrder.find(type => availableTypes.includes(type))
    }

    get formData(): VdaFormData {
        const {
            vin,
            licensePlate,
            documents,
            placement,
            customer,
            vinCheckDigit
        } = this
        return {
            vin: vin,
            licensePlate,
            documents,
            placement,
            customer,
            vinCheckDigit
        }
    }

    set formData(data: VdaFormData) {
        const {
            vin,
            licensePlate,
            documents,
            placement,
            customer,
            vinCheckDigit
        } = data
        this.vin = vin
        this.vinCheckDigit = vinCheckDigit

        const updated = this.licensePlate !== licensePlate
        this.licensePlate = licensePlate
        updateLicensePlates(documents, updated, {leadingLicensePlate: this.leadingLicensePlate, licensePlate: this.licensePlate})

        this.documents = sortDocuments(documents)
        this.placement = placement
        this.customer = customer
    }


    async reset() {
        const {data} = this.data || {}
        if (data) {
            this.vin = data.vin
            this.vinCheckDigit = data.vinCheckDigit
            this.documents = sortDocuments(data.documents)
            let licensePlate: string | undefined
            for (const d of data.documents) {
                switch (d.type) {
                    case 'COR_I':
                        licensePlate = d.licensePlate
                        break
                    case 'COR_II':
                        licensePlate = d.licensePlate
                        break
                    case 'LICENSE_PLATE_FRONT':
                        licensePlate = d.licensePlate
                        break
                    case 'LICENSE_PLATE_REAR':
                        licensePlate = d.licensePlate
                        break
                }
            }
            this.licensePlate = licensePlate || '';
            this.customer = this.getCustomerFromStoreById(data.customerId)

            const placement = await this.getPlacementFromStoreById(data.placementProcessId)
            this.valid = Boolean(placement)
            this.placement = placement
            this.initFormData = this.formData
        }
    }

    private getCustomerFromStoreById(customerId: string): VdaCustomerItem | undefined {
        const customerItem = vdaCustomerStore.items.filter(e => e.id === customerId)[0]
        return customerItem || {
            id: customerId,
            label: `Unknown (${customerId})`
        };
    }

    private async getPlacementFromStoreById(placementId: string): Promise<VdaPlacementItem | undefined> {
        var item: VdaPlacementItem | undefined = vdaPlacementStore.items.filter(e => e.id === placementId)[0]

        if (!item){
            item =  await vdaPlacementStore.loadPlacementProcess(placementId)
        }

        return item
    }
}

const vdaDetailStore = new VdaDetailStore()

export {
    vdaDetailStore
}
